<!--
  ~ This file is part of VloX.
  ~
  ~ Copyright (c) TROTALO, SAS. All Rights Reserved.
  ~
  ~ For complete copyright and license information, see the COPYRIGHT and LICENSE
  ~ files found in the top-level directory of this distribution.
  -->

<script setup>
  import { computed, onMounted, reactive, watch } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import { useAppInitialization } from './shared/composables/useAppInitialization'
  import BcsConnectionStatus from './shared/components/BcsConnectionStatus'
  import BcsServerStatus from './shared/components/BcsServerStatus'
  import { getAuth, onAuthStateChanged } from 'firebase/auth'
  import { getDatabase, ref as dbRef, onValue } from 'firebase/database'
  import { instancesRunning, setInstanceStatus } from './router/index.js'
  import { useIdleTimer } from './shared/composables/useIdleTimer'
  import IdleShutdownAlert from './shared/components/IdleShutdownAlert.vue'
  import { isInstanceManagementEnabled } from './shared/utils/instanceManagementEnvironment'
  import { useAuthStore } from './shared/stores/AuthStore'

  const router = useRouter()
  const route = useRoute()
  const { isAppReady, initializeApp } = useAppInitialization()

  // --- Idle timer & instance status (only active in UAT builds) ---
  const idleTimer = isInstanceManagementEnabled ? reactive(useIdleTimer()) : null
  const auth = isInstanceManagementEnabled ? getAuth() : null
  const shouldShowIdleShutdownAlert = computed(() => {
    return isInstanceManagementEnabled && !!idleTimer && route.name !== 'instanceStartup'
  })

  function syncIdleTimer(user = auth?.currentUser || null) {
    if (!idleTimer) return

    const shouldRunIdleTimer = !!user && instancesRunning.value && route.name !== 'instanceStartup'

    if (shouldRunIdleTimer) {
      idleTimer.start()
      return
    }

    idleTimer.stop()
  }

  if (isInstanceManagementEnabled) {
    // Listen to RT DB immediately (not in onMounted) so the router guard
    // has the status before the first navigation resolves
    const db = getDatabase()
    const statusRef = dbRef(db, 'SYSTEM_PARAMS/MIN_INSTANCES/status')
    onValue(statusRef, (snapshot) => {
      const rawStatus = snapshot.val()
      const running = rawStatus === 'running'
      setInstanceStatus(running)
      syncIdleTimer()
      if (!running) {
        router.replace({ name: 'instanceStartup' })
      }
    })

    watch(() => route.name, () => {
      syncIdleTimer()
    })

    // Start/stop idle timer based on auth state (needs onMounted for lifecycle)
    onMounted(() => {
      onAuthStateChanged(auth, (user) => {
        syncIdleTimer(user)
      })
    })
  }
  // --- End idle timer ---

  /*
   * The code step lives on /login. Any API call that answers 403 mfa_required flags the
   * store from the RequestService interceptor; routing happens here, where the router is already
   * injected (doing it in the interceptor would close the cycle router → AuthStore → service).
   */
  const authStore = useAuthStore()

  watch(() => authStore.mfaPending, (pending) => {
    if (pending && route.name !== 'login') {
      router.replace({ name: 'login' })
    }
  })

  onMounted(async () => {
    await initializeApp()

    /* Another tab finished verifying: pick up the refreshed session instead of leaving this
     * one stranded on a code step that no longer applies. */
    window.addEventListener('storage', async (event) => {
      if (event.key !== 'bcs.mfaVerifiedAt' || !event.newValue) return
      if (!(await authStore.refreshMfaPending())) {
        if (route.name === 'login') router.replace({ name: 'home' })
      }
    })
  })
</script>

<template>
  <!-- Main application content (only shown when fully initialized) -->
  <div v-if="isAppReady">
    <router-view />
  
    <!-- Connection status modal (will be hidden by default) -->
    <BcsConnectionStatus />

    <!-- Backend outage modal (will be hidden by default) -->
    <BcsServerStatus />

    <!-- UAT: Idle shutdown alert -->
    <IdleShutdownAlert
      v-if="shouldShowIdleShutdownAlert"
      :show="idleTimer.showWarning"
      :countdown-seconds="idleTimer.countdownSeconds"
      :is-stopping="idleTimer.isStopping"
      @cancel="idleTimer.cancelShutdown()"
      @stop-now="idleTimer.stopInstances()"
    />
  </div>
</template>

<style lang="scss">
  body.body--light {
    color: black;
  }

  .q-parallax__media > img {
    min-width: 50% !important;
  }

  .card {
    height: 100%;
  }

  .hand-cursor {
    cursor: pointer;
  }
</style>
